import React, { useState, useEffect,  } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, IconButton, LinearProgress, Typography, CircularProgress, Tooltip } from '@mui/material';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { DashboardButton } from '../components/Elements/DashboardTile';
import { FaPlus, FaRegEye, FaTrash, FaDownload, FaUpload } from 'react-icons/fa';
import { DataGridPro, GridToolbarDensitySelector, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarContainer, GridColDef, getGridStringOperators, GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { RiExpandUpDownLine } from 'react-icons/ri';
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { UsersModel } from '../models/UsersModel';
import { AddRequestSignature } from '../components/AddEdit/AddRequestSignature';
import Swal from 'sweetalert2';
import { formatUtcToLocal } from '../helper/utils';
import { StyledDropzone } from '../components/Elements/Dropzone';
import JSZip from 'jszip';
import { PartnerType, initPartner } from '../models/PartnersModel';
import { LightBlueBtnColor, DarkBlueBtnColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';

const DateFormat = 'MM-DD-YYYY';

export interface SignatureRequestModel {
    signatureRequestId: number;
    partnerId: number;
    partnerName: string;
    state: string;
    stateId: number;

    signerUserId: number;
    signerUserName: string;
    signerUserFirstName: string;
    signerUserLastName: string;

    dueDate: string;
    formType: string;
    signerName: string;
    
    sentOn: string;
    dateSigned: string;
    isSigned: boolean;
    sentBy: number;
    sentByUserName: string;
    sentByUserFirstName: string;
    sentByUserLastName: string;
    isWetSignature?: boolean;
    isElectronicSignature?: boolean;
    isDigitalSignature?: boolean;
    notarizationRequired: boolean;
    isDocsUploaded?: boolean;
};
export interface SignatureRequestDocumentModel {
    signatureRequestId: number;
    signatureRequestDocumentId: number;
    documentName: string;
    documentUrl: string;
    fileSizeKB: string;
    isSigned: boolean;
    dateSigned: string;
    createdByUserId: number;
    createdByUsername: string;
    createdDate: string;
};
export interface StatesModel {
    stateId: number;
    stateName: string;
    postalAbbv: string;
};
export interface PartnerModel {
    partnersId: number;
    customerName: string;
};

export const ClientUserRoles: string[] = [
    'Customer User',
    'Client Admin',
    'Client Processors',
    'State Reporter',
];

export const RequestSignature: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const userRoles: string[] = user?.userRoles?.split(",").map(role => role.trim()) || [];
    const IsUserClient = Boolean(ClientUserRoles.some(role => userRoles.includes(role)));
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
    const [viewDocumentLoadingUrl, setViewDocumentLoadingUrl] = useState<string | null>(null);
    const [partners, setPartners] = useState<PartnerType[]>([initPartner]);
    const [states, setStates] = useState<StatesModel[]>([{ stateId: 0, stateName: "", postalAbbv: "" }]);
    const [users, setUsers] = useState<UsersModel[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<SignatureRequestModel[]>([]);
    const [signatureRequests, setSignatureRequests] = useState<SignatureRequestModel[]>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const [addRequestSignatureOpen, setAddRequestSignatureOpen] = useState(false);

    const [documentList, setDocumentList] = useState<SignatureRequestDocumentModel[]>([]);
    const [documentListOpen, setDocumentListOpen] = useState(false);
    const [signatureRequestId, setSignatureRequestId] = useState(0);

    // Document viewer state
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [viewingDocument, setViewingDocument] = useState(false);

    useEffect(() => {
        getPartners();
        getStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSignatureRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const signatureRequestIdParam = searchParams.get('signatureRequestId');
        const openAttachments = searchParams.get('openAttachments') === 'true';

        if (signatureRequestIdParam && openAttachments) {
            const id = parseInt(signatureRequestIdParam, 10);
            if (!isNaN(id) && id > 0) {
                window.history.replaceState({}, '', window.location.pathname);
                setSignatureRequestId(id);
                viewDocumentsAttached(id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPartners = () => {
        setLoading(true);
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getStates = () => {
        setLoading(true);
        apiClient('/States/getStates', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setStates(responseJson.data);
        }).catch(error => {
            console.error("Error fetching states:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getUsers = (partnersId: number = 0) => {
        setLoadingUsers(true);
        let queryParams = 'isCustomerUser=true';
        if (partnersId > 0) {
            queryParams += `&partnersId=${partnersId}`;
        }
        queryParams += `&isActive=true`;
        apiClient(`/Users/getAllUsers?${queryParams}`, {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setUsers(responseJson.data);
        }).catch(error => {
            console.error("Error fetching users:", error)
        }).finally(() => {
            setLoadingUsers(false);
        });
    };
    const fetchSignatureRequests = async () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API
        const filterModelJson = filterModel && filterModel.items && filterModel.items.length > 0 ? filterModel : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        apiClient('/SignatureRequests/getAllSignatureRequests', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ 
                pageSize: pageSize, 
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson
            }),
        }).then((response: any) => {
            if (!response || !response.data) return;
            // Server-side filtering handles IsUserClient filtering, so no client-side filtering needed
            setSignatureRequests(response.data.signatureRequests);
            setTotalCount(response.data.totalCount);
        }).catch((error: any) => {
            console.error('Error fetching signature requests:', error);
            setSignatureRequests([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        })
    };

    const addNewSignatureRequest = () => {
        setSelectedRows([]);
        setAddRequestSignatureOpen(true);
    };
    const handleCloseAddRequestSignature = (isReload: boolean = false) => {
        setAddRequestSignatureOpen(false);
        if(isReload) {
            // reload the signature requests
            fetchSignatureRequests();
        }
        setSelectedRows([]);
    };
    const deleteSelectedSignatureRequests = () => {
        if(selectedRows.length === 0) {
            Swal.fire({
                title: 'No Signature Requests Selected',
                text: 'Please select at least one signature request to delete',
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: '<div style="color: white;">This will delete the selected signature requests</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Yes, Delete</div>',
            cancelButtonText: '<div style="color: white;">No, Continue</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                const signatureRequestIdList = selectedRows.map(row => row.signatureRequestId).join(',');
                // call api to delete the signature requests
                apiClient(`/SignatureRequests/deleteSignatureRequests?signatureRequestIdList=${signatureRequestIdList}`, {
                    method: 'DELETE',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                }).then((response) => {
                    if (!response) return;
                    if (response.status) {
                        // reload the signature requests
                        fetchSignatureRequests();
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Success',
                            text: 'The signature requests have been deleted successfully',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    }
                }).catch((error) => {
                    console.error('Error deleting signature requests:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to delete signature requests',
                        icon: 'error',
                        showCancelButton: true,
                        confirmButtonText: 'OK',
                    });
                });
            }
        }).catch((error) => {
            console.error('Error deleting signature requests:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete signature requests',
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: 'OK',
            });
        });
    };
    const viewDocumentsAttached = (signatureRequestId: number, openDialog: boolean = true) => {
        setViewLoadingId(signatureRequestId);
        setLoading(true);
        try {
            apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            }).then((response) => {
                if (!response) {
                    setLoading(false);
                    setViewLoadingId(null);
                    return;
                }
                const { data } = response;
                // Store the document list
                setDocumentList(data.documents);
                
                // show the list dialog, even if multiple documents
                if(openDialog) {
                    setDocumentListOpen(true);
                    // Clear loader after dialog opens (small delay to ensure dialog is rendered)
                    setTimeout(() => {
                        setLoading(false);
                        setViewLoadingId(null);
                    }, 100);
                } else {
                    setLoading(false);
                    setViewLoadingId(null);
                }
            }).catch((error) => {
                console.error("Error fetching documents:", error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to fetch documents',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                setViewLoadingId(null);
            });
        } catch (error) {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            setLoading(false);
            setViewLoadingId(null);
        }
    };
    const uploadSignedDocument = (files: File[]) => {
        if(files.length === 0) {
            Swal.fire({
                title: 'No Files Selected',
                text: 'Please select at least one file to upload',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({ 
            title: '<div style="color: white;">Are you sure?</div>',
            html: '<div style="color: white;">This will upload the selected files to MISC</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Yes, Upload</div>',
            cancelButtonText: '<div style="color: white;">No, Continue</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                files.forEach(file => {
                    formData.append('files', file);
                });
                formData.append('signatureRequestId', signatureRequestId.toString());
                // call api to upload the files
                apiClient(`/SignatureRequests/uploadSignedDocument`, {
                    method: 'POST',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: formData,
                }).then((response) => {
                    if (!response) return;
                    if (response.status) {
                        // reload the signature requests
                        fetchSignatureRequests();
                        // setSelectedRows([]);
                        handleCloseDocumentList();
                        Swal.fire({
                            title: 'Success',
                            text: 'The files have been uploaded successfully',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    }
                }).catch((error) => {
                    console.error('Error uploading files:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to upload files',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                });
            }
        }).catch((error) => {
            console.error('Error uploading files:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to upload files',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        });
    };

    const downloadRequestDocumentListToZip = async (signatureRequestId: number) => {
        setLoading(true);
        
        // Show loading dialog while fetching documents
        Swal.fire({
            title: 'Loading Documents...',
            html: 'Please wait while we load the documents.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            // Fetch documents directly
            const response = await apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents) {
                Swal.close();
                Swal.fire({
                    title: 'No Documents Found',
                    text: 'There are no documents available to download for this signature request.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documentsToDownload: SignatureRequestDocumentModel[] = response.data.documents;
            
            if(documentsToDownload.length === 0) {
                Swal.close();
                Swal.fire({
                    title: 'No Documents Available',
                    text: 'There are no documents available to download.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const selectedSignatureRequest = signatureRequests.find(request => request.signatureRequestId === signatureRequestId);
            const folderName = `${selectedSignatureRequest?.signerUserName} - ${selectedSignatureRequest?.formType} - ${selectedSignatureRequest?.partnerName}`;
            
            Swal.close();
            
            const confirmResult = await Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            });
            
            if (!confirmResult.isConfirmed) {
                setLoading(false);
                return;
            }
            
            // Show loading dialog for download preparation
            Swal.fire({
                title: 'Preparing Download...',
                html: 'Please wait while we prepare your files for download.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
            
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;
            const errors: string[] = [];
            const usedFileNames = new Set<string>();
            
            // Helper function to generate unique filename (Chrome-style numbering)
            const generateUniqueFileName = (baseFileName: string): string => {
                const fileExtension = baseFileName.includes('.') 
                    ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                    : '';
                const fileNameWithoutExt = baseFileName.includes('.')
                    ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                    : baseFileName;
                
                let uniqueFileName = baseFileName;
                let counter = 0;
                
                // Keep trying until we find a unique name
                while (usedFileNames.has(uniqueFileName)) {
                    counter++;
                    uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                }
                
                usedFileNames.add(uniqueFileName);
                return uniqueFileName;
            };
            
            // Generate unique filenames for all files first
            const filesWithUniqueNames = documentsToDownload.map((document: SignatureRequestDocumentModel) => ({
                document,
                uniqueFileName: generateUniqueFileName(document.documentName || `document_${document.signatureRequestDocumentId}`)
            }));
            
            // Fetch each file and add to zip
            await Promise.all(
                filesWithUniqueNames.map(async ({ document, uniqueFileName }) => {
                    try {
                        // Fetch the file from documentUrl
                        const response = await fetch(document.documentUrl, {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'omit',
                            cache: 'no-cache',
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }

                        // Get the blob (preserves original content type)
                        const blob = await response.blob();
                        
                        // Add file to zip
                        zip.file(uniqueFileName, blob);
                        successCount++;
                    } catch (error: any) {
                        failCount++;
                        const errorMsg = error.message || 'Failed to download';
                        errors.push(`${document.documentName}: ${errorMsg}`);
                        console.error(`Error downloading ${document.documentName} from ${document.documentUrl}:`, error);
                        console.error('Error details:', error);
                    }
                })
            );

            if (successCount === 0) {
                Swal.close();
                throw new Error('Failed to download any documents');
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folderName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.close();
            
            // Show success/partial success message
            if (failCount > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Partial Success',
                    html: `
                        <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                        <p><strong>${failCount}</strong> document(s) failed to download.</p>
                        ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                    `,
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `All ${successCount} document(s) have been downloaded successfully.`,
                    confirmButtonText: 'OK',
                });
            }
        } catch (error: any) {
            console.error('Error in downloadRequestDocumentListToZip:', error);
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
        }
    };

    const filterOperators = getGridStringOperators().filter(({ value }) => ['contains'].includes(value));
    const columns: GridColDef[] = [
        { field: 'dueDate', headerName: 'Due Date', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators
        },
        { field: 'state', headerName: 'State', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators 
        },
        { field: 'formType', headerName: 'Form Type', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators 
        },
        { field: 'signerUserName', headerName: 'Signer', 
            valueGetter: (params) => `${params.row.signerUserFirstName ?? ''} ${params.row.signerUserLastName ?? ''}`.trim(),
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators 
        },
        { field: 'partnerName', headerName: 'Client', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators 
        },
        { field: 'sentOn', headerName: 'Sent On', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators,
            renderCell: (params) => (
                <div>{formatUtcToLocal(params.value, DateFormat)}</div>
            )
        },
        { field: 'sentByUserName', headerName: 'Sent By', 
            valueGetter: (params) => `${params.row.sentByUserName ?? ''}`,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators 
        },
        ...(!IsUserClient ? [{ 
            field: 'dateSigned', headerName: 'Date Signed', 
            hideSortIcons: true,
            renderHeader: (params: any) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center' as const, headerAlign: 'center' as const, filterOperators, 
            renderCell: (params: any) => (
                <div>
                    {params.row.isSigned ? formatUtcToLocal(params.row.dateSigned, DateFormat) : 'N/A'}
                </div>
            )
        },] : []),
        { field: 'Action', headerName: 'Action', flex: 0.8, minWidth: 160, filterable: false, 
            headerAlign: 'center', align: 'center', disableExport: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                    <Tooltip title={IsUserClient ? "View & Upload Documents Attached" : "View Documents Attached"} arrow={true}>
                        <IconButton 
                            sx={{ color: LightBlueBtnColor }}
                            disabled={viewLoadingId === params.row.signatureRequestId}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    setSignatureRequestId(params.row.signatureRequestId);
                                    viewDocumentsAttached(params.row.signatureRequestId);
                                }
                            }}
                        >
                            {viewLoadingId === params.row.signatureRequestId ? (
                                <CircularProgress size={20} sx={{ color: LightBlueBtnColor }} />
                            ) : (
                                <FaRegEye />
                            )}
                        </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Upload Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    setSignatureRequestId(params.row.signatureRequestId);
                                    viewDocumentsAttached(params.row.signatureRequestId);
                                }
                            }}
                        ><FaUpload /></IconButton>
                    </Tooltip> */}
                    <Tooltip title="Download Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    downloadRequestDocumentListToZip(params.row.signatureRequestId);
                                }
                            }}
                        ><FaDownload /></IconButton>
                    </Tooltip>
                </Box>
            ) 
        },
    ];
    
    const handleCloseDocumentList = () => {
        setDocumentListOpen(false);
        setSelectedRows([]);
        setDocumentList([]);
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    };

    // Document viewer handlers
    const viewDocument = (documentUrl: string, documentName: string) => {
        setViewDocumentLoadingUrl(documentUrl);
        setViewingDocument(true);
        setDocumentViewerUrl(documentUrl);
        setDocumentViewerFileName(documentName);
        setDocumentViewerOpen(true);
        // Simulate loading delay if needed
        setTimeout(() => {
            setViewingDocument(false);
            setViewDocumentLoadingUrl(null);
        }, 500);
    };

    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
    };
    
    //#region UI Elements
    const getRowsPerPageOptions = (totalCount: number, pageSize: number) => {
        return [10, 25, 50, 100];
    };
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarExport className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        return (
            <div className=" p-2 d-flex justify-content-end datagrid-butons">
                <div className="ml-2"><CustomToolbar /></div>
            </div>
        );
    };
    //#endregion
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="d-flex align-items-center justify-content-between">
                <div className="card-title">
                    <span className="d-flex">Request a Signature</span>
                </div>
            </div>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>

            <Box sx={{ width: '100%' }}>
                {!IsUserClient && (
                    <Box sx={{ width: 'auto', display: 'flex', gap: 1, mb: 2 }}>
                        <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                            <DashboardButton title="New Request" 
                                onClick={addNewSignatureRequest} 
                                icon={<FaPlus />} 
                                variant="primary" 
                            />
                        </Box>
                        <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                            <DashboardButton title="Delete" 
                                onClick={deleteSelectedSignatureRequests} 
                                icon={<FaTrash />} 
                                variant="secondary" 
                            />
                        </Box>
                    </Box>
                )}

                {/* Signature Requests Table */}
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            {loading && <LinearProgress sx={{ backgroundColor: "#132D3D" }} />}
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro rows={signatureRequests}
                                    getRowId={(r) => r?.signatureRequestId ?? 0}
                                    columns={columns}

                                    page={currentPage - 1}
                                    pageSize={pageSize}
                                    pagination
                                    rowCount={totalCount}
                                    paginationMode="server"
                                    onPageChange={(newPage) => setCurrentPage(newPage + 1)}
                                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                    rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
                                    filterMode="server"
                                    filterModel={filterModel}
                                    onFilterModelChange={(newFilterModel) => {
                                        setFilterModel(newFilterModel);
                                        setCurrentPage(1); // Reset to first page when filter changes
                                    }}
                                    sortingMode="server"
                                    sortingOrder={['asc', 'desc']}
                                    sortModel={sortModel}
                                    onSortModelChange={(newSortModel) => {
                                        setSortModel(newSortModel);
                                        setCurrentPage(1); // Reset to first page when sort changes
                                    }}
                                    loading={loading}
                                    checkboxSelection={!IsUserClient ? true : false}
                                    selectionModel={selectedRows.map(row => row.signatureRequestId).filter((id): id is number => id !== undefined)}
                                    onSelectionModelChange={(newSelectionModel) => {
                                        // Map the selected IDs to full ReportTagModel objects
                                        const selectedObjects = (newSelectionModel as number[])
                                            .map(id => signatureRequests.find(request => request.signatureRequestId === id))
                                            .filter((request): request is SignatureRequestModel => request !== undefined);
                                        setSelectedRows(selectedObjects);
                                    }}
                                    
                                    components={{
                                        Toolbar: EditToolbar,
                                    }}
                                    sx={{
                                        border: '0px',
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ width: '80%' }}>
                <AddRequestSignature Open={addRequestSignatureOpen} 
                    handleClose={handleCloseAddRequestSignature} 
                    partners={partners} states={states} 
                    users={users}
                    loadingUsers={loadingUsers}
                    onClientChange={(partnersId) => getUsers(partnersId)}
                />
            </Box>

            <Box sx={{ width: '80%' }}>
                <ViewAttachments open={documentListOpen} handleClose={handleCloseDocumentList} 
                    documentList={documentList}
                    loading={loading}
                    IsUserClient={IsUserClient}
                    uploadSignedDocument={uploadSignedDocument}
                    downloadRequestDocumentListToZip={downloadRequestDocumentListToZip}
                    viewDocument={viewDocument}
                    viewDocumentLoadingUrl={viewDocumentLoadingUrl}
                />
            </Box>

            {/* Document Viewer Dialog */}
            <Dialog open={documentViewerOpen} 
                onClose={handleCloseDocumentViewer}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle className="card-title">
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}>
                        <Box sx={{
                            flex: 1,
                            fontWeight: "medium",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: "16px",
                        }}>
                            {documentViewerFileName || "Document Viewer"}
                        </Box>
                        <IconButton onClick={handleCloseDocumentViewer}
                            sx={{
                                color: "#666",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    color: "#000",
                                },
                            }}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: 0, height: "80vh", position: "relative" }}>
                    {viewingDocument ? (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : documentViewerUrl ? (
                        <Box sx={{ width: "100%", height: "100%" }}>
                            <iframe title="Document Viewer"
                                src={documentViewerUrl}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                            />
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
};

interface ViewAttachmentsProps {
    open: boolean;
    handleClose: () => void;
    documentList: SignatureRequestDocumentModel[];
    loading: boolean;
    IsUserClient: boolean;
    uploadSignedDocument: (files: File[]) => void;
    downloadRequestDocumentListToZip: (signatureRequestId: number) => void;
    viewDocument: (documentUrl: string, documentName: string) => void;
    viewDocumentLoadingUrl: string | null;
};
const ViewAttachments: React.FC<ViewAttachmentsProps> = ({ open, handleClose, documentList, loading, IsUserClient, uploadSignedDocument, downloadRequestDocumentListToZip, viewDocument, viewDocumentLoadingUrl }) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    
    const closeAttachmentsDialog = () => {
        handleClose();
        setSelectedFiles([]);
    };
    
    const columns: GridColDef[] = [
        { field: 'documentName', headerName: 'Document Name', 
            flex: 3, minWidth: 250, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'createdByUsername', headerName: 'Sent By', 
            flex: 1.5, minWidth: 120, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {params.value ?? 'N/A'}
                </div>
            )
        },
        { field: 'createdDate', headerName: 'Date Uploaded', 
            flex: 0.8, minWidth: 130, align: 'center', headerAlign: 'center', type: 'date', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {formatUtcToLocal(params.value, DateFormat)}
                </div>
            )
        },
        { field: 'action', headerName: 'Download', 
            hideSortIcons: true,
            type: 'string', 
            flex: 0.6, minWidth: 120, align: 'center', headerAlign: 'center', 
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                    <Tooltip title="View Document" arrow={true}>
                        <IconButton 
                            sx={{ color: LightBlueBtnColor }}
                            disabled={viewDocumentLoadingUrl === params.row.documentUrl}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.documentUrl && params.row.documentName) {
                                    viewDocument(params.row.documentUrl, params.row.documentName);
                                }
                            }} 
                        >
                            {viewDocumentLoadingUrl === params.row.documentUrl ? (
                                <CircularProgress size={20} sx={{ color: LightBlueBtnColor }} />
                            ) : (
                                <FaRegEye />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    downloadRequestDocumentListToZip(params.row.signatureRequestId);
                                }
                            }}
                        ><FaDownload /></IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];
    
    return (
        <Dialog open={open} onClose={closeAttachmentsDialog} maxWidth="lg" fullWidth>
            <DialogTitle className="card-title">
                {IsUserClient ? 'View & Upload Attachments' : 'View Attachments'}
            </DialogTitle>
            <DialogContent>
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            {loading && <LinearProgress sx={{ backgroundColor: "#132D3D" }} />}
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro rows={documentList}
                                    getRowId={(r) => r?.signatureRequestDocumentId ?? 0}
                                    columns={columns}
                                    sx={{
                                        border: '0px',
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
                
                {/* upload documents area for client users */}
                {IsUserClient && (
                <Box sx={{  }}>
                    <DialogContent>
                        <DialogTitle className="card-subtitle"
                            sx={{
                                textAlign: 'center',
                            }}
                        >Upload a signed document and send to MISC</DialogTitle>
                        <FormHelperText className='formtexthelper-heading'>Upload Documents</FormHelperText>
                        <StyledDropzone label="Upload a signed document and send to MISC"
                            onFileDrop={(files: File[]) => {
                                setSelectedFiles([...selectedFiles, ...files]);
                            }}
                        />
                        {selectedFiles && selectedFiles.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <FormHelperText className='formtexthelper-heading'>
                                    Selected Files ({selectedFiles.length}):
                                </FormHelperText>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedFiles.map((file, index) => (
                                        <Chip key={index} label={file.name} onDelete={() => {
                                            setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                        }} deleteIcon={<CancelIcon />} color="primary" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Box>
                )}
            </DialogContent>
            <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={closeAttachmentsDialog}>Close</button>
                {IsUserClient && (
                <button className="btn-primary active-btn-blue search-btn" type="button" 
                    onClick={() => {uploadSignedDocument(selectedFiles);}} 
                    disabled={selectedFiles.length === 0}
                    style={{
                        opacity: selectedFiles.length === 0 ? 0.5 : 1,
                        cursor: selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                        maxWidth: 'none',
                        minWidth: 'auto',
                        padding: '8.5px 33px',
                        whiteSpace: 'nowrap',
                    }}
                >Send to MISC</button>
                )}
            </DialogActions>
        </Dialog>
    );
};
export default RequestSignature;